import { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Box, Container, IconButton, Skeleton } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import PatientsIcon from 'icons/PatientsIcon';
import { formatDateTime, toTitleCase } from 'common/utils';
import ImageCarousel from 'components/modals/ImageCarousel/ImageCarousel';
import { useGetFlaggedUsers } from 'hooks/useUsers';
import { IPhotoSize } from 'types/types';
import useGlobalStyles from 'styles/globalStyles';
import PersonIcon from '@mui/icons-material/Person';
import UserStatus from 'components/statuses/UserStatus';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import UserActionButtons from 'components/tables/ActionButtons/UserActionButtons';
import { tableColumnProps } from 'common/constants';


// One row per flagged account: how often it was refused, the last time and
// which count ran out, and how many phones it has been on.
export default function FlaggedUsersTable() {
  const globalStyles = useGlobalStyles();
  const [carouselImages, setCarouselImages] = useState<IPhotoSize[]>([]);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rowCount, setRowCount] = useState(0);

  const requestParams = {
    page: page + 1,
    size,
  };


  const { data, refetch, isLoading } = useGetFlaggedUsers(requestParams, {
    onSuccess: () => { },
    onError: () => console.log('error'),
  });


  const columns: GridColDef[] = [
    {
      field: 'fullName',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Full name" />
      ),
      renderCell: (params) => {
        return (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Box>

              {params?.row?.avatar ? (
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCarouselImages([params.row.avatar]);
                    setCarouselOpen(true);
                  }}
                >
                  <Box
                    component={'img'}
                    src={params?.row?.avatar.small}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '5px',
                    }}
                  />
                </IconButton>
              ) : (
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '5px',
                    backgroundColor: colors.topBackground,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PersonIcon sx={{ color: colors.lightgray, fontSize: 22 }} />
                </Box>
              )}
            </Box>
            <CustomText
              text={`${toTitleCase(params?.row?.fullName)}`}
              fontSize={15}
              fontWeight={500}
              color={colors.black}
            />
          </Box >
        )
      }
    },
    {
      field: 'phone',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text='Phone' />
      ),
      renderCell: (params) => {
        return (
          <Box>
            <CustomCellValue text={`+${params.row.phones[0].phoneCode}${params.row.phones[0].number}`} />
          </Box>
        )
      }
    },
    {
      field: 'hits',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <CustomCellLabel text='Times refused' />,
      renderCell: (params) => <CustomCellValue text={`${params.row.hits}`} />,
    },
    {
      field: 'lastHitAt',
      ...tableColumnProps,
      renderHeader: () => <CustomCellLabel text='Last refused' />,
      renderCell: (params) => <CustomCellValue text={formatDateTime(params.row.lastHitAt)} />,
    },
    {
      field: 'ranOut',
      ...tableColumnProps,
      renderHeader: () => <CustomCellLabel text='Limit reached' />,
      // Whose count ran out - the account's or its phone's - and over what.
      renderCell: (params) => (
        <CustomCellValue text={`${params.row.countedBy === 'phone' ? 'Phone' : 'Account'}, ${params.row.period === 'day' ? 'daily' : 'hourly'}`} />
      ),
    },
    {
      field: 'phonesUsed',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <CustomCellLabel text='Phones used' />,
      renderCell: (params) => <CustomCellValue text={`${params.row.phonesUsed}`} />,
    },
    {
      field: 'status',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text='Status' />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            {<UserStatus status={params.row.status} />}
          </Box>
        )
      },
    },
    {
      field: 'actions',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomText
          text="Actions"
          fontSize={15}
          fontWeight={600}
          color={colors.secondary}
        />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <UserActionButtons
              id={params.row.id}
              refetch={refetch}
              user={params.row}
            />
          </Box>
        )
      },
    }
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);


  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          gap={2}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <PatientsIcon color={colors.primary} />
            </Box>
            <CustomText text="Flagged" fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>
        </Box>


        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          disableRowSelectionOnClick
          onRowSelectionModelChange={(selection) => {
            console.log('Selected row IDs:', selection);
          }}
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          onRowClick={(params) => setSelectedId(params.row.id)}
          className={globalStyles.dataGrid}
        />
      </Paper>
      <ImageCarousel
        open={carouselOpen}
        images={carouselImages}
        onClose={() => setCarouselOpen(false)}
      />

      <Box mt={3} />
    </Container>
  );
}
